/**
 * PDF Text Extraction Service
 *
 * Production service for extracting text content with bounding box coordinates
 * from PDF files using pdf.js.
 *
 * Supports: Hebrew (RTL), Arabic (RTL), and English (LTR) text
 *
 * Linear Issue: AIF-4
 */

import { pdfjs } from 'react-pdf';

// Ensure worker is configured
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

/**
 * Extract text content with bounding box coordinates from a PDF file
 *
 * @param {File|string} pdfSource - PDF File object or URL
 * @returns {Promise<Array>} Array of text items with coordinates
 *
 * Each text item contains:
 * - text: string - The actual text content
 * - x: number - X coordinate (left edge)
 * - y: number - Y coordinate (top edge, converted from PDF bottom-up to top-down)
 * - width: number - Width of text bounding box
 * - height: number - Height of text bounding box
 * - page: number - Page number (1-indexed)
 * - fontName: string - Font name used
 * - fontSize: number - Font size in points
 * - dir: string - Text direction ('ltr' or 'rtl')
 */
export async function extractTextWithCoordinates(pdfSource) {
  try {
    // Convert File object to ArrayBuffer for pdf.js
    const pdfData = pdfSource instanceof File
      ? { data: await pdfSource.arrayBuffer() }
      : pdfSource;

    const loadingTask = pdfjs.getDocument(pdfData);
    const pdf = await loadingTask.promise;

    const allTextItems = [];
    const numPages = pdf.numPages;

    console.log(`[PDF Extraction] Loaded ${numPages} page(s)`);

    // Iterate through all pages
    for (let pageNum = 1; pageNum <= numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const viewport = page.getViewport({ scale: 1 });

      // Extract text content with coordinates
      const textContent = await page.getTextContent();

      console.log(`[PDF Extraction] Page ${pageNum} - Found ${textContent.items.length} text item(s)`);

      // Process each text item
      textContent.items.forEach((item) => {
        // Transform matrix: [scaleX, skewY, skewX, scaleY, translateX, translateY]
        const transform = item.transform;

        // Extract position and dimensions
        const x = transform[4]; // translateX
        const y = viewport.height - transform[5]; // translateY (convert from PDF coords to top-down)

        // Detect text direction (RTL for Hebrew/Arabic, LTR for English)
        const dir = item.dir || 'ltr';

        allTextItems.push({
          text: item.str,
          x: Math.round(x * 100) / 100,
          y: Math.round(y * 100) / 100,
          width: Math.round(item.width * 100) / 100,
          height: Math.round(item.height * 100) / 100,
          page: pageNum,
          fontName: item.fontName || 'unknown',
          fontSize: Math.round(transform[3] * 100) / 100, // scaleY gives font size
          dir: dir,
          hasEOL: item.hasEOL || false, // End of line marker
        });
      });
    }

    console.log(`[PDF Extraction] Total text items extracted: ${allTextItems.length}`);
    return allTextItems;

  } catch (error) {
    console.error('[PDF Extraction] Error:', error);
    throw new Error(`Failed to extract text from PDF: ${error.message}`);
  }
}

/**
 * Group text items into lines based on Y-coordinate proximity
 * Useful for detecting form field labels that span multiple words
 *
 * @param {Array} textItems - Array of text items from extractTextWithCoordinates
 * @param {number} tolerance - Y-coordinate tolerance for grouping (default: 5)
 * @returns {Array} Array of text lines with combined text and bounding boxes
 */
export function groupTextIntoLines(textItems, tolerance = 5) {
  if (!textItems || textItems.length === 0) return [];

  const lines = [];
  let currentLine = {
    text: '',
    items: [],
    x: Infinity,
    y: 0,
    width: 0,
    height: 0,
    page: textItems[0].page,
    dir: textItems[0].dir
  };

  // Sort by page, then by Y, then by X
  const sortedItems = [...textItems].sort((a, b) => {
    if (a.page !== b.page) return a.page - b.page;
    if (Math.abs(a.y - b.y) > tolerance) return a.y - b.y;
    return a.x - b.x;
  });

  sortedItems.forEach((item, index) => {
    const isNewPage = item.page !== currentLine.page;
    const isNewLine = Math.abs(item.y - currentLine.y) > tolerance || isNewPage;

    if (isNewLine && currentLine.items.length > 0) {
      // Finalize current line
      lines.push({ ...currentLine });

      // Start new line
      currentLine = {
        text: item.text,
        items: [item],
        x: item.x,
        y: item.y,
        width: item.width,
        height: item.height,
        page: item.page,
        dir: item.dir
      };
    } else {
      // Add to current line
      currentLine.text += (currentLine.text ? ' ' : '') + item.text;
      currentLine.items.push(item);
      currentLine.x = Math.min(currentLine.x, item.x);
      currentLine.y = currentLine.items.length > 0
        ? currentLine.items.reduce((sum, i) => sum + i.y, 0) / currentLine.items.length
        : item.y;
      currentLine.width = Math.max(...currentLine.items.map(i => i.x + i.width)) - currentLine.x;
      currentLine.height = Math.max(...currentLine.items.map(i => i.height));
    }

    // Push last line
    if (index === sortedItems.length - 1 && currentLine.items.length > 0) {
      lines.push(currentLine);
    }
  });

  return lines;
}

/**
 * Find potential form field labels in extracted text
 * Looks for patterns like "שם פרטי:", "First Name:", "الاسم الأول:"
 *
 * @param {Array} textLines - Array of text lines from groupTextIntoLines
 * @returns {Array} Array of detected field labels with coordinates
 */
export function detectFieldLabels(textLines) {
  const fieldLabels = [];

  // Common patterns for form field labels (Hebrew, Arabic, English)
  const labelPatterns = [
    // Hebrew patterns
    /שם\s*פרטי|שם\s*משפחה|תעודת\s*זהות|כתובת|טלפון|תאריך|חתימה|עיר|רחוב|מיקוד/i,
    // Arabic patterns
    /الاسم|اللقب|الهوية|العنوان|الهاتف|التاريخ|التوقيع|المدينة|الشارع|الرمز البريدي/i,
    // English patterns
    /first\s*name|last\s*name|full\s*name|id\s*number|address|phone|date|signature|city|street|zip/i,
  ];

  textLines.forEach((line) => {
    const hasLabelPattern = labelPatterns.some(pattern => pattern.test(line.text));
    const endsWithColon = line.text.trim().endsWith(':') || line.text.trim().endsWith('_');
    const isShort = line.text.length < 50; // Labels are usually short

    if ((hasLabelPattern || endsWithColon) && isShort) {
      fieldLabels.push({
        label: line.text.trim(),
        x: line.x,
        y: line.y,
        width: line.width,
        height: line.height,
        page: line.page,
        dir: line.dir,
        confidence: hasLabelPattern ? 0.9 : 0.6
      });
    }
  });

  return fieldLabels;
}

/**
 * Main service function: Extract and analyze PDF
 * Combines extraction, grouping, and label detection
 *
 * @param {File} pdfFile - PDF File object from file input
 * @returns {Promise<Object>} Analysis results with textItems, lines, and labels
 */
export async function analyzePDF(pdfFile) {
  try {
    // Extract raw text items
    const textItems = await extractTextWithCoordinates(pdfFile);

    // Check if PDF has text content
    if (textItems.length === 0) {
      throw new Error('No text content found. This appears to be a scanned/image-based PDF. Text-based PDFs are required for field detection.');
    }

    // Group into lines
    const lines = groupTextIntoLines(textItems);

    // Detect field labels
    const labels = detectFieldLabels(lines);

    // Language detection
    const hasHebrew = textItems.some(item => /[\u0590-\u05FF]/.test(item.text));
    const hasArabic = textItems.some(item => /[\u0600-\u06FF]/.test(item.text));
    const hasEnglish = textItems.some(item => /[a-zA-Z]/.test(item.text));

    return {
      textItems,
      lines,
      labels,
      languages: { hasHebrew, hasArabic, hasEnglish }
    };

  } catch (error) {
    console.error('[PDF Analysis] Error:', error);
    throw error;
  }
}

export default {
  extractTextWithCoordinates,
  groupTextIntoLines,
  detectFieldLabels,
  analyzePDF
};
